// Selector de stage de un ticket del CRM (POST /tickets/:id/move).
//
// Réplica del "Cambiar stage" del detalle de ticket del CRM
// (crm/apps/web/src/routes/TicketDetailPage.tsx), que hasta ahora no existía en
// este SPA: el único cambio de stage era el drag&drop del tablero, así que desde
// la ficha del lead (y desde el detalle del ticket) el único botón visible era
// "Archivar" y los asesores archivaban tickets cuando lo que querían era
// CERRARLOS. Son cosas distintas:
//
//   - Cerrar  = mover a un stage con ticketState='closed'. El CRM sella
//               closedAt server-side al mover (tickets.repo.ts) — no hay
//               endpoint "close", cerrar ES mover.
//   - Archivar= flag aparte (POST /tickets/:id/archive): saca el ticket de
//               listados y tablero. Es un soft-delete, no un desenlace.
//
// Por eso agrupamos las opciones en <optgroup> "Abiertos"/"Cerrados": el select
// del CRM pinta solo `s.label` y no distingue, que es justo lo que hace difícil
// encontrar "cerrar". El dato (`ticketState`) ya viaja en el payload de stages.

import { useCallback, useEffect, useMemo, useState } from "react";
import { CrmHttpError, describeCrmError } from "../../api/crm";
import { loadTicketStages, moveTicket } from "../../api/crmTickets";
import { TicketStage } from "../../api/crmTypes";
import { useCrmResource } from "../../hooks/useCrmResource";

export default function TicketStageSelect({
  ticketId,
  pipelineId,
  stageId,
  stageLabel,
  onMoved,
  compact = false,
}: {
  ticketId: string;
  pipelineId: string;
  /** Stage actual según el servidor. Al cambiar, limpia el valor optimista. */
  stageId: string;
  /** Etiqueta del stage actual: se pinta mientras cargan las opciones. */
  stageLabel: string;
  /** Recargar el recurso del llamante tras un move (204 sin cuerpo). */
  onMoved: () => void;
  /** Variante densa para el panel del lead (filas estrechas del aside). */
  compact?: boolean;
}) {
  const stages = useCrmResource<TicketStage[]>(
    () => loadTicketStages(pipelineId),
    [pipelineId],
    "No se pudieron cargar los stages",
  );

  // Valor pintado tras un move OK mientras el llamante recarga: el 204 ya
  // confirma el cambio server-side, así que no es una apuesta — evita que el
  // select vuelva al stage viejo durante el refetch. Se limpia solo cuando
  // `stageId` llega actualizado (efecto de abajo) o si el move falla.
  const [optimistic, setOptimistic] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setOptimistic(null);
  }, [stageId]);

  const onChange = useCallback(
    async (next: string) => {
      if (next === stageId || pending) return;
      setPending(true);
      setError(null);
      setOptimistic(next);
      try {
        await moveTicket(ticketId, { stageId: next });
        onMoved();
      } catch (err) {
        setOptimistic(null);
        setError(
          err instanceof CrmHttpError
            ? err.status === 403
              ? "No tienes permisos para mover tickets (requiere rol ops o admin en el CRM)."
              : describeCrmError(err)
            : "Error de conexión con el CRM.",
        );
      } finally {
        setPending(false);
      }
    },
    [ticketId, stageId, pending, onMoved],
  );

  // El BFF ya devuelve los stages activos ordenados por posición; filtramos y
  // ordenamos igualmente (mismo criterio defensivo que Tickets/index.tsx) para
  // no depender del orden de la respuesta al partir por estado.
  const { open, closed } = useMemo(() => {
    const active = (stages.data ?? [])
      .filter((s) => !s.archived)
      .sort((a, b) => a.position - b.position);
    return {
      open: active.filter((s) => s.ticketState === "open"),
      closed: active.filter((s) => s.ticketState === "closed"),
    };
  }, [stages.data]);

  const value = optimistic ?? stageId;
  const loaded = stages.data !== null;
  // El stage actual puede no estar entre las opciones (stage archivado tras
  // asignarse, o error de carga): sin esta opción de respaldo el <select>
  // pintaría el primer option y mentiría sobre dónde está el ticket.
  const known = loaded && [...open, ...closed].some((s) => s.id === value);

  const font = compact ? ".74rem" : ".85rem";

  return (
    <div>
      <select
        value={value}
        disabled={pending || !loaded || !!stages.error}
        onChange={(e) => void onChange(e.target.value)}
        aria-label="Cambiar stage del ticket"
        title={
          stages.error ??
          "Cambiar el stage. Para CERRAR el ticket, elige un stage de 'Cerrados' (archivar no es cerrar)."
        }
        style={{
          width: "100%",
          padding: compact ? "0.2rem 0.3rem" : "0.3rem 0.4rem",
          borderRadius: "0.4rem",
          border: "1px solid #dee2e6",
          background: pending ? "#f8f9fa" : "white",
          color: "#1f2933",
          fontSize: font,
          cursor: pending || !loaded ? "default" : "pointer",
          opacity: pending ? 0.7 : 1,
        }}
      >
        {!known && (
          <option value={value}>
            {stages.loading ? `${stageLabel} — cargando…` : stageLabel}
          </option>
        )}
        {open.length > 0 && (
          <optgroup label="Abiertos">
            {open.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </optgroup>
        )}
        {closed.length > 0 && (
          <optgroup label="Cerrados">
            {closed.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </optgroup>
        )}
      </select>
      {error && (
        <div
          style={{
            fontSize: ".7rem",
            color: "#b32424",
            marginTop: "0.2rem",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
